import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { 
  BarChart3, 
  TrendingUp, 
  Car, 
  Users, 
  MapPin, 
  Fuel, 
  Wrench, 
  DollarSign, 
  AlertTriangle,
  Download
} from 'lucide-react';
import { relatoriosService } from '../../services/relatorios';
import { exportService } from '../../services/export';
import { formatCurrency, formatNumber } from '../../utils/formatters';
import toast from 'react-hot-toast';
import { mensagemDoErro } from '../../utils/erros';

const RelatoriosDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [periodo, setPeriodo] = useState('mes');

  const { data, isLoading, error } = useQuery({
    queryKey: ['dashboard', periodo],
    queryFn: () => relatoriosService.getDashboard(periodo),
    placeholderData: (previousData) => previousData
  });

  const dashboardData = data?.data;

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !dashboardData) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600 mb-4">Erro ao carregar dashboard</p>
        <button 
          onClick={() => window.location.reload()}
          className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Tentar novamente
        </button>
      </div>
    );
  }

  const cards = [
    {
      title: 'Veículos',
      value: dashboardData.veiculos.total,
      subtitle: `${dashboardData.veiculos.ativos} ativos`,
      icon: Car,
      color: 'bg-blue-500',
      href: '/veiculos'
    },
    {
      title: 'Motoristas',
      value: dashboardData.motoristas.total,
      subtitle: `${dashboardData.motoristas.ativos} ativos`,
      icon: Users,
      color: 'bg-green-500',
      href: '/motoristas'
    },
    {
      title: 'Viagens',
      value: dashboardData.viagens.total,
      subtitle: `${dashboardData.viagens.concluidas} concluídas`,
      icon: MapPin,
      color: 'bg-purple-500',
      href: '/viagens'
    },
    {
      title: 'Custos Totais',
      value: formatCurrency(dashboardData.custos.total),
      subtitle: 'Este período',
      icon: DollarSign,
      color: 'bg-red-500',
      href: '/despesas'
    },
    {
      title: 'Abastecimentos',
      value: dashboardData.abastecimentos.total,
      subtitle: `${formatNumber(dashboardData.abastecimentos.litros_total)} litros`,
      icon: Fuel,
      color: 'bg-yellow-500',
      href: '/abastecimentos'
    },
    {
      title: 'Manutenções',
      value: dashboardData.manutencoes.total,
      subtitle: `${dashboardData.manutencoes.preventivas} preventivas`,
      icon: Wrench,
      color: 'bg-orange-500',
      href: '/manutencoes'
    }
  ];

  const relatorios = [
    {
      title: 'Dashboard Geral',
      description: 'Visão geral de todos os indicadores',
      icon: BarChart3,
      color: 'bg-blue-500',
      href: '/dashboard'
    },
    {
      title: 'Consumo de combustível',
      description: 'Análise detalhada de consumo e custos',
      icon: Fuel,
      color: 'bg-yellow-500',
      href: '/abastecimentos'
    },
    {
      title: 'Manutenções',
      description: 'Relatório de manutenções e custos',
      icon: Wrench,
      color: 'bg-orange-500',
      href: '/manutencoes'
    },
    {
      title: 'Despesas',
      description: 'Análise de despesas por categoria',
      icon: DollarSign,
      color: 'bg-red-500',
      href: '/despesas'
    },
    {
      title: 'Viagens',
      description: 'Relatório de viagens e rotas',
      icon: MapPin,
      color: 'bg-purple-500',
      href: '/viagens'
    },
    {
      title: 'Eficiência por veículo',
      description: 'Análise de performance por veículo',
      icon: TrendingUp,
      color: 'bg-green-500',
      href: '/relatorios'
    },
    {
      title: 'Alertas e vencimentos',
      description: 'Documentos e licenças vencendo',
      icon: AlertTriangle,
      color: 'bg-red-600',
      href: '/alertas'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard de relatórios</h1>
          <p className="text-gray-600">Visão geral e análise de dados</p>
        </div>
        <div className="flex items-center space-x-4">
          <select
            value={periodo}
            onChange={(e) => setPeriodo(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="dia">Hoje</option>
            <option value="semana">Esta Semana</option>
            <option value="mes">Este Mês</option>
            <option value="ano">Este Ano</option>
          </select>
          <button
            onClick={async () => {
              try { await exportService.geralPdf(periodo); toast.success('PDF gerado!'); }
              catch (err: any) { toast.error(mensagemDoErro(err, 'Erro ao gerar PDF')); }
            }}
            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
            <Download className="w-4 h-4 mr-2" />
            Exportar PDF
          </button>
        </div>
      </div>

      {/* Cards de Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {cards.map((card, index) => (
          <div
            key={index}
            className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow cursor-pointer"
            onClick={() => navigate(card.href)}
          >
            <div className="flex items-center">
              <div className={`p-3 rounded-full ${card.color} text-white`}>
                <card.icon className="w-6 h-6" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">{card.title}</p>
                <p className="text-2xl font-bold text-gray-900">{card.value}</p>
                <p className="text-sm text-gray-500">{card.subtitle}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Gráficos e Estatísticas */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Custos por Categoria */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Custos por categoria</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-blue-500 rounded-full mr-3"></div>
                <span className="text-sm text-gray-600">Combustível</span>
              </div>
              <span className="text-sm font-medium">{formatCurrency(dashboardData.custos.combustivel)}</span>
            </div>
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-orange-500 rounded-full mr-3"></div>
                <span className="text-sm text-gray-600">Manutenção</span>
              </div>
              <span className="text-sm font-medium">{formatCurrency(dashboardData.custos.manutencao)}</span>
            </div>
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-red-500 rounded-full mr-3"></div>
                <span className="text-sm text-gray-600">Despesas</span>
              </div>
              <span className="text-sm font-medium">{formatCurrency(dashboardData.custos.despesas)}</span>
            </div>
            <div className="border-t pt-4">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-900">Total</span>
                <span className="text-lg font-bold text-gray-900">{formatCurrency(dashboardData.custos.total)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Status dos Veículos */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Status dos veículos</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                <span className="text-sm text-gray-600">Ativos</span>
              </div>
              <span className="text-sm font-medium">{dashboardData.veiculos.ativos}</span>
            </div>
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-red-500 rounded-full mr-3"></div>
                <span className="text-sm text-gray-600">Inativos</span>
              </div>
              <span className="text-sm font-medium">{dashboardData.veiculos.inativos}</span>
            </div>
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-yellow-500 rounded-full mr-3"></div>
                <span className="text-sm text-gray-600">Em Manutenção</span>
              </div>
              <span className="text-sm font-medium">{dashboardData.veiculos.manutencao}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Relatórios Disponíveis */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Relatórios disponíveis</h3>
          <p className="text-sm text-gray-600">Acesse relatórios detalhados e análises específicas</p>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {relatorios.map((relatorio, index) => (
              <div
                key={index}
                className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-md transition-all cursor-pointer"
                onClick={() => navigate(relatorio.href)}
              >
                <div className="flex items-center mb-3">
                  <div className={`p-2 rounded-full ${relatorio.color} text-white mr-3`}>
                    <relatorio.icon className="w-5 h-5" />
                  </div>
                  <h4 className="font-medium text-gray-900">{relatorio.title}</h4>
                </div>
                <p className="text-sm text-gray-600 mb-3">{relatorio.description}</p>
                <div className="flex items-center text-sm text-blue-600">
                  <span>Acessar relatório</span>
                  <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Alertas Rápidos */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <AlertTriangle className="w-5 h-5 text-red-500 mr-2" />
            Alertas e vencimentos
          </h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">{dashboardData.motoristas.cnh_vencendo}</div>
              <div className="text-sm text-gray-600">CNHs Vencendo</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-600">{dashboardData.despesas.pendentes}</div>
              <div className="text-sm text-gray-600">Despesas Pendentes</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{dashboardData.viagens.em_andamento}</div>
              <div className="text-sm text-gray-600">Viagens em Andamento</div>
            </div>
          </div>
          <div className="mt-4 text-center">
            <button
              onClick={() => navigate('/alertas')}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              Ver Todos os Alertas
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RelatoriosDashboard;
